import type { IntakeFormSchema } from '@rx-manager/shared';
import { CHECKBOX, Field, Input, cn } from './ui';
import type { FieldErrors } from '../lib/validate';
import type { Translate } from '../lib/i18n';

/**
 * The service's own health questions, rendered from its `formSchema`.
 *
 * The schema is data a pharmacist typed, so every field here is a known type
 * from a closed set — never `dangerouslySetInnerHTML`, never an eval'd
 * expression. A booking page that renders arbitrary pharmacy-supplied markup is
 * a stored-XSS hole aimed at patients.
 *
 * The labels are the pharmacist's own words and are shown as written, in
 * whatever language they typed them. Translating clinical screening questions
 * automatically would be worse than leaving them alone.
 */
export function IntakeForm({
  schema,
  answers,
  errors,
  onChange,
  t,
}: {
  schema: IntakeFormSchema;
  answers: Record<string, unknown>;
  errors: FieldErrors;
  onChange: (id: string, value: unknown) => void;
  t: Translate;
}) {
  return (
    <div className="space-y-5">
      {schema.fields.map((field) => {
        const id = `intake-${field.id}`;
        const value = answers[field.id];
        const error = errors[field.id];

        if (field.type === 'checkbox') {
          // Its own layout: a checkbox's label belongs beside it, and wrapping
          // the control in the label makes the whole line a hit target.
          const describedBy = [
            field.help ? `${id}-help` : null,
            error ? `${id}-error` : null,
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <div key={field.id} className="space-y-1.5">
              <label
                htmlFor={id}
                className="flex min-h-[2.75rem] cursor-pointer items-start gap-3"
              >
                <input
                  id={id}
                  type="checkbox"
                  checked={value === true}
                  onChange={(event) => onChange(field.id, event.target.checked)}
                  aria-describedby={describedBy || undefined}
                  aria-invalid={Boolean(error)}
                  aria-required={field.required || undefined}
                  className={CHECKBOX}
                />
                <span className="text-base text-foreground">{field.label}</span>
              </label>
              {field.help && (
                <p id={`${id}-help`} className="ml-8 text-sm text-muted-foreground">
                  {field.help}
                </p>
              )}
              {error && (
                <p id={`${id}-error`} className="ml-8 text-sm text-danger">
                  {t(error)}
                </p>
              )}
            </div>
          );
        }

        return (
          <Field
            key={field.id}
            id={id}
            label={field.label}
            help={field.help}
            error={error ? t(error) : undefined}
            required={field.required}
            requiredLabel={t('details.required')}
          >
            {(props) =>
              field.type === 'textarea' ? (
                <textarea
                  {...props}
                  rows={3}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) => onChange(field.id, event.target.value)}
                  className={cn(
                    'min-h-12 w-full rounded-lg border border-input bg-card px-3.5 py-2.5 text-base shadow-sm',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
                    'aria-[invalid=true]:border-danger',
                  )}
                />
              ) : field.type === 'select' ? (
                <select
                  {...props}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) => onChange(field.id, event.target.value)}
                  className={cn(
                    'min-h-12 w-full rounded-lg border border-input bg-card px-3.5 text-base shadow-sm',
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
                    'aria-[invalid=true]:border-danger',
                  )}
                >
                  <option value="" />
                  {(field.options ?? []).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  {...props}
                  type={field.type === 'date' ? 'date' : 'text'}
                  value={typeof value === 'string' ? value : ''}
                  onChange={(event) => onChange(field.id, event.target.value)}
                />
              )
            }
          </Field>
        );
      })}
    </div>
  );
}
